import { Checkbox, Empty, Pagination } from 'antd';
import classNames from 'classnames';
import { size, throttle } from 'lodash';
import React from 'react';
import { useMemo } from 'react';

import SortIcon from './SortIcon';

const CHECK_COLUMN = {
  columnKey: 'checkColumn',
  className: 'cell-y-center',
  renderHeader: (v, rowData, { onChangeSelectAll }) => {
    return <Checkbox checked={v} onClick={onChangeSelectAll} />;
  },
  render: (v, rowData, { onChangeSelectItem }) => {
    return (
      <Checkbox checked={v} onClick={(e) => onChangeSelectItem(rowData, e)} />
    );
  },
  width: '3rem',
};

const renderCellDefault = (value) => {
  return <span title={value}>{value ?? ''}</span>;
};

const renderHeaderDefault = (value, column) => {
  return <span title={column.tooltip || value}>{value ?? ''}</span>;
};

const getSort = (v, key) => {
  if (!v) {
    return `+${key}`;
  }

  if (v.startsWith('+')) {
    return `-${key}`;
  }

  return '';
};

const SmartTable = (props) => {
  const {
    config,
    dataSource = [],
    onScrollBottom = () => undefined,
    onChangeSort,
    tableRef,
    selected = [],
    onChangeSelected = () => undefined,
    onHoverRow = () => undefined,
    onClickRow = () => undefined,
    onLeaveRow = () => undefined,
    onChangePage = () => undefined,
    onChangeSelectedAll = () => undefined,
    metaData = {},
    rowFixed,
  } = props;
  const {
    columns,
    mainKey,
    headerHeight = 50,
    rowHeight = 50,
    tableStyle,
    hasCheck,
    hasPagination,
    pageSize = 10,
    backgroundCheck = true,
    sortMulti = false,
    rowFixedHeight
  } = config;
  const { sort = '', skip = 0, totalItems } = metaData;
  const sortsParam = sort.split(';');
  const currentPage = hasPagination ? skip / pageSize : 0;

  const columnsFinal = useMemo(() => {
    if (hasCheck) {
      return [CHECK_COLUMN, ...columns];
    }
    return columns;
  }, [columns, hasCheck]);

  const onChangeSelectItem = (rowData, e) => {
    e.stopPropagation();
    onChangeSelectRow(rowData, e.target.checked);
  };

  const onChangeSelectRow = (rowData, check) => {
    onClickRow(rowData);
    if (!hasCheck) return;
    if (check) {
      return onChangeSelected([...selected, rowData]);
    }

    return onChangeSelected(
      selected.filter((item) => item[mainKey] !== rowData[mainKey])
    );
  };

  const onChangeSelectAll = (e) => {
    onChangeSelectedAll(e.target.checked);
    if (e.target.checked) {
      return onChangeSelected(dataSource);
    }
    onChangeSelected([]);
  };

  const renderCell = (column, rowData, index) => {
    const { render = renderCellDefault, columnKey } = column;
    if (columnKey === 'checkColumn') {
      return render(
        selected.find((item) => item[mainKey] === rowData[mainKey]),
        { ...rowData, index },
        { ...props, onChangeSelectItem }
      );
    }
    return render(
      rowData[columnKey],
      { ...rowData, index: index + currentPage * pageSize },
      props
    );
  };

  const onChangeCellSort = (column) => () => {
    const { getFieldSort, columnKey } = column;
    const key = getFieldSort ? getFieldSort() : columnKey;
    let sortsList = [...sortsParam];
    let index = sortsList.findIndex(
      (item) => item.substring(1, item.length) === key
    );

    if (index >= 0) {
      sortsList[index] = getSort(sortsList[index], key);
    } else {
      sortsList = [...sortsList, getSort(null, key)];
    }

    let text = '';
    if (sortMulti) {
      text = sortsList.filter(Boolean).join(';');
    } else {
      if (index >= 0) {
        text = sortsList[index];
      } else {
        text = sortsList[sortsList.length - 1];
      }
    }

    onChangeSort(text);
  };

  const selectedKeys = selected.map((item) => item[mainKey]);
  const isCheckAll = dataSource.every((item) =>
    selectedKeys.find((s) => s === item[mainKey])
  );

  const renderCellHeader = (column) => {
    const {
      renderHeader = renderHeaderDefault,
      title,
      isSort,
      columnKey,
      getFieldSort,
    } = column;
    const key = getFieldSort ? getFieldSort() : columnKey;
    return (
      <>
        {columnKey === 'checkColumn'
          ? renderHeader(isCheckAll, column, { ...props, onChangeSelectAll })
          : renderHeader(title, column, props)}
        {isSort && (
          <span className="smart-table__sort">
            <SortIcon
              value={sortsParam.find(
                (item) => item.substring(1, item.length) === key
              )}
            />
          </span>
        )}
      </>
    );
  };

  const getColumnWidth = (column) => {
    const { width = 'fit-content' } = column;
    return { width };
  };

  const onScroll = throttle((e) => {
    const el = e.target;
    if (el.scrollTop && el.scrollHeight - el.scrollTop === el.clientHeight) {
      if (onScrollBottom) onScrollBottom();
    }
  }, 300);

  const dataView = hasPagination
    ? dataSource.slice(
        pageSize * currentPage,
        pageSize * currentPage + pageSize
      )
    : dataSource;

  return (
    <div className="smart-table-block" onScroll={onScroll} ref={tableRef}>
      <div className="smart-table-container" style={tableStyle}>
        <table className="smart-table">
          <thead
            className="smart-table__header"
            style={{ height: headerHeight }}
          >
            <tr>
              {columnsFinal.map((column) => (
                <th key={column.columnKey} style={getColumnWidth(column)}>
                  <div
                    className="smart-table__cell"
                    onClick={column.isSort && onChangeCellSort(column)}
                  >
                    {renderCellHeader(column)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="smart-table__body">
            {dataSource?.length > 0 &&
              dataView.map((rowData, index) => {
                const checked = selectedKeys.find(
                  (k) => k === rowData[mainKey]
                );
                return (
                  <tr
                    key={rowData[mainKey]}
                    className={classNames('smart-table__row', {
                      'smart-table__row--check': checked && backgroundCheck,
                    })}
                    onClick={() => onChangeSelectRow(rowData, !checked)}
                    onMouseOver={() => onHoverRow(rowData)}
                    onMouseLeave={() => onLeaveRow(rowData)}
                  >
                    {columnsFinal.map((column) => (
                      <td key={column.columnKey} style={getColumnWidth(column)}>
                        <div
                          className={classNames('smart-table__cell', {
                            [column.className]: column.className,
                          })}
                          style={{ height: rowHeight }}
                        >
                          {renderCell(column, rowData, index + 1)}
                        </div>
                      </td>
                    ))}
                  </tr>
                );
              })}
            {rowFixed &&
              size(rowFixed) > 0 && dataSource?.length > 0 &&
              rowFixed.map((rowData, index) => (
                <tr
                  className={classNames(
                    'smart-table__row',
                    'smart-table__total-row'
                  )}
                  onMouseOver={() => onHoverRow(rowData)}
                  onMouseLeave={() => onLeaveRow(rowData)}
                  style={{ '--bottom': `${(size(rowFixed) - index - 1) * (rowFixedHeight || rowHeight) }px`}}
                >
                  {columnsFinal.map((column) => (
                    <td key={column.columnKey} style={getColumnWidth(column)}>
                      <div
                        className={classNames('smart-table__cell', {
                          [column.className]: column.className,
                        })}
                        style={{ height: rowFixedHeight || rowHeight }}
                      >
                        {renderCell(column, rowData, size(dataSource) + 1)}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
        {dataSource?.length === 0 && (
          <div className="smart-table__empty">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        )}
        {hasPagination && dataSource.length > 0 && totalItems > pageSize && (
          <div className="pagination-mediation">
            <Pagination
              onChange={(v) => onChangePage(v - 1)}
              current={currentPage + 1}
              total={totalItems}
              showSizeChanger={false}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(
  React.forwardRef((props, ref) => {
    return <SmartTable {...props} tableRef={ref} />;
  })
);
